import type { GetMyProfileResponse } from '@massilia/queries'

import { useAppForm } from '@massilia/features'
import { useUpdateMyPhoneNumbersMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { updatePhoneNumbersSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'

import { DisplayRow } from './display-row'
import { EditableSection } from './editable-section'

interface PhoneNumbersSectionProps {
  profile: GetMyProfileResponse
}

const PHONE_TYPE_OPTIONS = [
  { label: 'Principal', value: 'PRINCIPAL' },
  { label: 'Fixe', value: 'LANDLINE' },
  { label: 'Travail', value: 'WORK' },
  { label: 'Autre', value: 'OTHER' },
] as const

const PHONE_TYPE_LABELS: Record<string, string> = {
  LANDLINE: 'Fixe',
  OTHER: 'Autre',
  PRINCIPAL: 'Principal',
  WORK: 'Travail',
}

interface PhoneEntry {
  id?: string
  number: string
  type: string
}

export function PhoneNumbersSection({ profile }: PhoneNumbersSectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync: updatePhoneNumbers } = useUpdateMyPhoneNumbersMutation()

  const phoneNumbers = profile.client?.phoneNumbers ?? []

  const form = useAppForm({
    defaultValues: {
      phoneNumbers: (phoneNumbers.length > 0
        ? phoneNumbers.map((pn) => ({ id: pn.id, number: pn.number, type: pn.type }))
        : [{ id: undefined, number: '', type: 'PRINCIPAL' }]) as PhoneEntry[],
    },
    onSubmit: async ({ value }) => {
      try {
        const validPhones = value.phoneNumbers.filter((pn) => pn.number.trim())
        await updatePhoneNumbers({
          phoneNumbers: validPhones.map((pn) => ({
            id: pn.id,
            number: pn.number,
            type: pn.type,
          })),
        })
        setIsEditing(false)
      } catch (error) {
        toast.error(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          updatePhoneNumbersSchema
        ),
    },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  return (
    <EditableSection
      actionLabel="Modifier les numéros"
      isEditing={isEditing}
      onToggleEdit={() => (isEditing ? handleCancel() : setIsEditing(true))}
      editForm={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField name="phoneNumbers" mode="array">
            {(field) => (
              <div className="flex flex-col gap-3">
                {field.state.value.map((_, index) => (
                  <div key={index} className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="w-full sm:w-40">
                      <form.AppField
                        name={`phoneNumbers[${index}].type`}
                        children={(subField) => (
                          <subField.SelectField
                            label={index === 0 ? 'Type' : ''}
                            options={PHONE_TYPE_OPTIONS}
                          />
                        )}
                      />
                    </div>
                    <div className="flex flex-1 items-end gap-2">
                      <div className="flex-1">
                        <form.AppField
                          name={`phoneNumbers[${index}].number`}
                          children={(subField) => (
                            <subField.PhoneNumberField
                              label={index === 0 ? 'Numéro' : ''}
                            />
                          )}
                        />
                      </div>
                      {field.state.value.length > 1 && (
                        <Button
                          className="mb-0.5 shrink-0 text-red-500 hover:text-red-700"
                          onClick={() => field.removeValue(index)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  className="text-brand-600 hover:text-brand-700 flex items-center gap-1 self-start text-sm font-medium"
                  onClick={() => field.pushValue({ id: undefined, number: '', type: 'PRINCIPAL' })}
                  type="button"
                >
                  <Plus className="size-4" />
                  Ajouter un numéro
                </button>
              </div>
            )}
          </form.AppField>

          <div className="flex gap-3">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <>
                  <Button
                    className="bg-brand-600 hover:bg-brand-700 text-white"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        </form>
      }
    >
      <div className="flex flex-col">
        {phoneNumbers.length === 0 ? (
          <DisplayRow label="Numéro de téléphone" value={undefined} />
        ) : (
          phoneNumbers.map((pn, index) => (
            <DisplayRow
              key={pn.id}
              label={`Numéro de téléphone #${index + 1}`}
              sublabel={PHONE_TYPE_LABELS[pn.type] ?? pn.type}
              value={pn.number}
            />
          ))
        )}
      </div>
    </EditableSection>
  )
}
